import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Constant from "../../../constant/constant";
import "./ExamResult.css";
import "./Questions.css";

const BATCHES = ["1", "2", "3", "4", "5"];
const LETTERS = ["A", "B", "C", "D"];

const emptyForm = (batch = "") => ({
  batch,
  question: "",
  optionA: "",
  optionB: "",
  optionC: "",
  optionD: "",
  correctAnswer: "",
});

const ExamQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState("all");
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState(emptyForm());
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const apiBase = `${Constant.API_BASE_URL}sentinix/api/exam/questions`;
  const defaultBatch = selectedBatch === "all" ? "" : selectedBatch;

  const fetchQuestions = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/admin/batch/${selectedBatch}`);
      setQuestions(res.data || []);
    } catch (err) {
      console.error("Error fetching questions:", err);
    }
  }, [apiBase, selectedBatch]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const visible = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return questions;
    return questions.filter((q) => (q.question || "").toLowerCase().includes(t));
  }, [questions, search]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await axios.put(`${apiBase}/${formData.id}`, formData);
        alert("Question updated successfully!");
      } else {
        await axios.post(apiBase, formData);
        alert("Question saved successfully!");
      }
      fetchQuestions();
      setShowModal(false);
    } catch (err) {
      console.error("Error saving question:", err);
      alert(err.response?.data?.error || "Error saving question!");
    }
  };

  const handleEdit = (q) => {
    setFormData({
      id: q.id,
      batch: q.batch || defaultBatch,
      question: q.question || "",
      optionA: q.options?.[0] ?? "",
      optionB: q.options?.[1] ?? "",
      optionC: q.options?.[2] ?? "",
      optionD: q.options?.[3] ?? "",
      correctAnswer: q.correctAnswer || "",
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const openAddModal = () => {
    setFormData(emptyForm(defaultBatch));
    setIsEditing(false);
    setShowModal(true);
  };

  // ---------- Excel import ----------
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
      // headers are matched loosely: "Option A", "optionA", "Correct Answer" ...
      const pick = (r, name) => {
        const key = Object.keys(r).find(
          (k) => k.toLowerCase().replace(/[^a-z]/g, "") === name
        );
        return key ? r[key] : "";
      };
      const parsed = rows
        .map((r) => ({
          batch: pick(r, "batch"),
          question: pick(r, "question"),
          optionA: pick(r, "optiona"),
          optionB: pick(r, "optionb"),
          optionC: pick(r, "optionc"),
          optionD: pick(r, "optiond"),
          correctAnswer: pick(r, "correctanswer"),
        }))
        .filter((q) => String(q.question).trim() !== "");
      if (parsed.length === 0) {
        alert("No questions found in the file.");
        return;
      }
      const note = replaceExisting
        ? "\n\nExisting questions in these batches will be DELETED and replaced."
        : "";
      if (!window.confirm(`Import ${parsed.length} questions?${note}`)) return;
      setImporting(true);
      const res = await axios.post(`${apiBase}/bulk`, {
        questions: parsed,
        replaceBatches: replaceExisting,
      });
      alert(`Imported ${res.data.imported} questions (batches: ${res.data.batches.join(", ")}).`);
      fetchQuestions();
    } catch (err) {
      const d = err.response?.data;
      alert(
        d?.error
          ? `${d.error}${d.details ? "\n" + d.details.join("\n") : ""}`
          : "Could not read or import the file."
      );
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Batch", "Question", "Option A", "Option B", "Option C", "Option D", "Correct Answer"],
      [1, "Sample question?", "Option A text", "Option B text", "Option C text", "Option D text", "A"],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Questions");
    XLSX.writeFile(wb, "questions_template.xlsx");
  };

  return (
    <div className="ea-page">
      <div className="ea-header">
        <div>
          <h2>❓ Exam Questions</h2>
          <p>Add, edit and import questions, and set the correct answer for each.</p>
        </div>
        <div className="ea-actions">
          <button className="ea-btn ea-btn-light" onClick={downloadTemplate}>
            ⬇ Template
          </button>
          <label className="qp-replace">
            <input
              type="checkbox"
              checked={replaceExisting}
              onChange={(e) => setReplaceExisting(e.target.checked)}
            />
            Replace existing in same batches
          </label>
          <button
            className="ea-btn ea-btn-light"
            disabled={importing}
            onClick={() => fileInputRef.current?.click()}
          >
            {importing ? "Importing..." : "📥 Import Excel"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={handleImportFile}
          />
          <button className="ea-btn ea-btn-white" onClick={openAddModal}>
            + Add Question
          </button>
        </div>
      </div>

      <div className="ea-card qp-toolbar">
        <div className="ea-field">
          <label>Batch</label>
          <select
            className="ea-select"
            value={selectedBatch}
            onChange={(e) => setSelectedBatch(e.target.value)}
          >
            <option value="all">All Batches</option>
            {BATCHES.map((b) => (
              <option key={b} value={b}>Batch {b}</option>
            ))}
          </select>
        </div>
        <div className="ea-field">
          <label>Search</label>
          <input
            className="ea-input"
            placeholder="Search in questions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="ea-card">
        <div className="ea-table-wrap">
          <table className="ea-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Batch</th>
                <th>Question</th>
                <th>Options</th>
                <th>Answer</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.length > 0 ? (
                visible.map((q, i) => (
                  <tr key={q.id ?? i}>
                    <td data-label="#" className="ea-muted">{i + 1}</td>
                    <td data-label="Batch">
                      <span className="ea-badge">{q.batch}</span>
                    </td>
                    <td data-label="Question" className="ea-wide qp-q">{q.question}</td>
                    <td data-label="Options" className="ea-wide">
                      <ul className="qp-options">
                        {LETTERS.map((l, idx) => (
                          <li key={l} className={q.correctAnswer === l ? "is-correct" : ""}>
                            <b>{l}.</b> {q.options?.[idx]}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td data-label="Answer">
                      <span className={`qp-answer ${q.correctAnswer ? "" : "none"}`}>
                        {q.correctAnswer || "-"}
                      </span>
                    </td>
                    <td data-label="Action">
                      <button className="ea-btn ea-btn-ghost ea-btn-sm" onClick={() => handleEdit(q)}>
                        ✏️ Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="ea-empty ea-empty-cell">
                    <div className="ea-empty-icon">🗂️</div>
                    No questions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {visible.length > 0 && (
          <div className="ea-count">Showing {visible.length} question(s)</div>
        )}
      </div>

      {showModal && (
        <div className="ea-overlay">
          <div className="ea-modal">
            <h3>{isEditing ? "✏️ Edit Question" : "➕ Add Question"}</h3>

            <form onSubmit={handleSubmit} className="qp-form">
              <div className="qp-row">
                <div className="ea-field">
                  <label>Batch</label>
                  <select
                    className="ea-select"
                    name="batch"
                    value={formData.batch}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select batch --</option>
                    {BATCHES.map((b) => (
                      <option key={b} value={b}>Batch {b}</option>
                    ))}
                  </select>
                </div>
                <div className="ea-field">
                  <label>Correct Answer</label>
                  <select
                    className="ea-select"
                    name="correctAnswer"
                    value={formData.correctAnswer}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select correct option --</option>
                    {LETTERS.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ea-field">
                <label>Question</label>
                <textarea
                  className="ea-textarea"
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  placeholder="Enter question..."
                  required
                  rows="3"
                />
              </div>

              <div className="qp-options-grid">
                {LETTERS.map((l) => (
                  <div className="ea-field" key={l}>
                    <label className="qp-opt-label">
                      Option {l}
                      {formData.correctAnswer === l && " ✅"}
                    </label>
                    <input
                      className="ea-input"
                      name={`option${l}`}
                      value={formData[`option${l}`]}
                      onChange={handleChange}
                      required
                    />
                  </div>
                ))}
              </div>

              <div className="qp-modal-actions">
                <button type="button" className="ea-btn ea-btn-ghost" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="ea-btn">
                  {isEditing ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamQuestions;
